// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import {
  ProviderAccessRejectedIllustration,
  ProviderAccessSuccessIllustration,
} from '@loa/ui-components';
import type { ProviderSubmissionResult } from '../../types/providerAccess';
import { ProviderAccessPageShell } from './ProviderAccessPageShell';
import { ProviderAccessStatusCard } from './ProviderAccessStatusCard';

type ProviderAccessSubmittedProps = Readonly<{
  result: ProviderSubmissionResult;
  /** Reserved for a future "Submit another" CTA — currently unused inside
   *  the component but accepted from callers (ProviderAccessPage) so the
   *  prop contract stays stable. */
  onSubmitAnother?: () => void;
  brokerageName?: string;
  brokerageLogoUrl?: string | null;
  /** When 'COA', shows Change of Agency wording instead of LOA wording */
  documentType?: 'LOA' | 'COA' | 'NEW_BUSINESS';
}>;

const successIcon = <ProviderAccessSuccessIllustration />;
const rejectedIcon = <ProviderAccessRejectedIllustration />;

export function ProviderAccessSubmitted({
  result,
  brokerageName,
  brokerageLogoUrl,
  documentType,
}: ProviderAccessSubmittedProps) {
  const isCoA = documentType === 'COA';
  const isCoaRejection = isCoA && result.rejectionReasonCode;

  const icon = isCoaRejection ? rejectedIcon : successIcon;

  let title: string;
  if (isCoaRejection) title = 'Change of Agency rejected';
  else if (isCoA) title = 'Change of Agency Accepted!';
  else title = 'Policy Details Submitted!';

  let message: string;
  if (isCoaRejection) {
    message = "It has been passed to the relevant financial advisor. You don't need to do anything else.";
  } else if (isCoA) {
    message = "Thanks for accepting the change of agency. It has been passed to the relevant financial advisor. You don't need to do anything else.";
  } else {
    message = "Thanks for submitting the policy details. It has been passed to the relevant financial advisor. You don't need to do anything else.";
  }

  return (
    <ProviderAccessPageShell
      brokerageName={brokerageName}
      brokerageLogoUrl={brokerageLogoUrl}
    >
      <ProviderAccessStatusCard
        icon={icon}
        title={title}
        message={message}
      />
    </ProviderAccessPageShell>
  );
}
